import { findIndex, isEmpty, uniq } from 'lodash/fp';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { useObjectMap } from 'dashboard/components/GridLayoutRender/useObjectMap';
import { Module } from 'dashboard/lib/module';
import { Account } from 'dashboard/models/account';
import { CaseQueryResult } from 'dashboard/models/case-query-result';
import { GraphQuery } from 'dashboard/models/graph-query';
import { Inquiry } from 'dashboard/models/inquiry';
import AISummary from 'dashboard/modules/AISummary';
// eslint-disable-next-line rulesdir/pds-migration
import Button from 'dashboard/modules/Button';
import CaseAttachments from 'dashboard/modules/CaseAttachments';
import CaseComments from 'dashboard/modules/CaseComments';
import CaseEmail from 'dashboard/modules/CaseEmail';
import CaseInfo from 'dashboard/modules/CaseInfo';
import { getCasesData } from 'dashboard/modules/CaseModuleBase/helpers';
import Checklist from 'dashboard/modules/Checklist';
import DataDisplay from 'dashboard/modules/DataDisplay';
import DataTableContextAdapter from 'dashboard/modules/DataTable';
import Device from 'dashboard/modules/Device';
import EventTimeline from 'dashboard/modules/EventTimeline';
import FieldUpdate from 'dashboard/modules/FieldUpdate';
import Graph from 'dashboard/modules/Graph';
import GraphV2 from 'dashboard/modules/GraphV2';
import Iframe from 'dashboard/modules/Iframe/Iframe';
// eslint-disable-next-line rulesdir/pds-migration
import Input from 'dashboard/modules/Input';
import InputSelect from 'dashboard/modules/InputSelect';
import InsightsSummary from 'dashboard/modules/InsightsSummary/InsightsSummary';
import KeyValue from 'dashboard/modules/KeyValue';
import LayoutByAccount from 'dashboard/modules/LayoutByAccount';
import ListMatches from 'dashboard/modules/ListMatches';
import Location from 'dashboard/modules/Location';
import Markdown from 'dashboard/modules/Markdown';
import RelatedCases from 'dashboard/modules/RelatedCases';
import Resource from 'dashboard/modules/Resource';
import SARModule from 'dashboard/modules/SAR';
import SimilaritySelfie from 'dashboard/modules/SimilaritySelfie';
import Table from 'dashboard/modules/Table';
import { Tags } from 'dashboard/modules/Tags/Tags';
import { useResolveCaseVariableQueryState } from 'dashboard/redux/api/cases';
import { CaseApi } from 'dashboard/views/DashboardCaseShowView/DashboardCaseShowView';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import { FieldType } from 'lib/fields';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export interface CaseModuleBaseProps<M extends Module> {
  module: M;
  caseApi: CaseApi;
  runCodeWithContext: (code: string) => Promise<unknown>;
  isPreview?: boolean;
  isPrint?: boolean;
}

export function CaseModuleBase<M extends Module>({
  module,
  caseApi,
  runCodeWithContext,
  isPreview = false,
  isPrint = false,
}: CaseModuleBaseProps<M>) {
  const {
    accounts,
    accountStatuses,
    accountTypes,
    allInquiryTemplates,
    assignee,
    case: kase,
    caseActionWorkflows,
    caseComments,
    caseDataManager,
    caseCommentTemplates,
    caseEmailTemplates,
    caseMessages,
    caseObjects,
    caseQueryResults,
    caseTemplateFieldSchema,
    caseTemplateStates,
    commentWorkflows,
    commentWorkflowStepActions,
    commentWorkflowRuns,
    currentEnvironment,
    devices,
    documents,
    featureFlags,
    inquiries,
    inquiryTemplates,
    inquiryTemplateVersions,
    inquiryVerifications,
    verifications,
    listItems,
    listMatches,
    lists,
    reportTemplates,
    sarNarrativeTemplates,
    sessions,
    tags,
    transactionStatuses,
    transactionTypes,
    users,
    isLoadingInquiriesDetails,
    isLoadingReportsDetails,
    addComment,
    editCaseComment,
    removeCaseComment,
    handleUpdateTags,
    handleUpdateInquiryTags,
    handleUpdateReportTags,
    handleAddCaseAttachment,
    handleDeleteCaseAttachment,
    handleToggleChecklistItem,
    handleUpdateFields,
    handleRefreshCaseQueryResult,
    handleRunCaseActionWorkflow,
    handleCreateEmailDraft,
    handleUpdateEmailDraft,
    handleSendEmail,
    handleDeleteEmail,
    handleCreateSAR,
    handleUpdateSAR,
    handleAutofillSAR,
    handleFlagInquiryIssues,
    handleFlagVerificationIssues,
    showMessage,
  } = caseApi;

  const objectMap = useObjectMap(caseObjects);

  const hasCaseWritePermission = usePermission(Permission.CaseWrite);

  const renderModule = () => {
    switch (module.type) {
      case ModuleTypes.CaseInfo: {
        const referenceIds = uniq(
          caseObjects
            .map((caseObject) => {
              if (
                caseObject.type === ApiModel.Account ||
                caseObject.type === ApiModel.Inquiry ||
                caseObject.type === ApiModel.Report ||
                caseObject.type === ApiModel.Transaction
              ) {
                return caseObject.attributes['reference-id'];
              }
            })
            .filter((value): value is string => value != null),
        );

        const accountIds = uniq(
          caseObjects
            .filter((caseObject) => caseObject.type === ApiModel.Account)
            .map((caseObject) => caseObject.id),
        );

        return (
          <CaseInfo
            module={module}
            case={kase}
            assignee={assignee}
            referenceIds={referenceIds}
            accountIds={accountIds}
          />
        );
      }
      case ModuleTypes.CaseComments:
        return (
          <CaseComments
            scope={`${module.token}_${kase.id}`}
            module={module}
            caseComments={caseComments}
            caseCommentTemplates={caseCommentTemplates}
            users={users}
            workflows={commentWorkflows}
            workflowActions={commentWorkflowStepActions}
            workflowRuns={commentWorkflowRuns}
            addComment={addComment}
            editCaseComment={editCaseComment}
            removeCaseComment={removeCaseComment}
            isPreview={isPreview}
            isPrint={isPrint}
          />
        );
      case ModuleTypes.Checklist: {
        return (
          <Checklist
            module={module}
            case={kase}
            states={caseTemplateStates}
            handleToggleChecklistItem={handleToggleChecklistItem}
            isPrint={isPrint}
          />
        );
      }
      case ModuleTypes.Tags: {
        const caseTags = tags.filter((tag) => {
          return findIndex((rt) => rt.id === tag.id, kase.relationships.tags.data) !== -1;
        });
        return (
          <Tags tags={caseTags} tagScope={ApiModel.Case} handleUpdateTags={handleUpdateTags} />
        );
      }
      case ModuleTypes.EventTimeline:
        return (
          <EventTimeline
            module={module}
            subject={kase}
            objects={[
              ...verifications,
              ...caseObjects,
              ...kase.relationships['case-messages'].data,
            ]}
            currentEnvironment={currentEnvironment}
          />
        );
      case ModuleTypes.Location:
        return (
          <Location
            module={module}
            isRedacted={false}
            caseObjects={caseObjects}
            sessions={sessions}
            verifications={verifications}
            // NOTE: sessions and verifications only come from inquiry details right now
            isLoading={isLoadingInquiriesDetails}
          />
        );
      case ModuleTypes.Device:
        return (
          <Device
            module={module}
            sessions={sessions}
            devices={devices}
            // NOTE: devices and session only come from inquiry details right now
            isLoading={isLoadingInquiriesDetails}
          />
        );
      case ModuleTypes.Graph: {
        // DEPRECATED - we only keep this around for when viewing archived cases that have it.
        return <Graph />;
      }
      case ModuleTypes.Graphv2: {
        const graphQueryCaseObjects = caseObjects.filter(
          (object) => object.type === ApiModel.GraphQuery,
        ) as GraphQuery[];
        const accountCaseObjects = caseObjects.filter(
          (object) => object.type === ApiModel.Account,
        ) as Account[];

        const { 'case-queries-to-rerun-on-exclude': caseQueryIds } = module;

        let relevantCaseQueryResults: CaseQueryResult[] = [];
        if (caseQueryIds && caseQueryIds.length > 0) {
          relevantCaseQueryResults = caseQueryResults.filter((caseQueryResult) => {
            return caseQueryIds.includes(caseQueryResult.relationships['case-query'].data.id);
          });
        }

        return (
          <GraphV2
            accounts={accountCaseObjects}
            caseQueryResults={relevantCaseQueryResults}
            attachedGraphQueries={graphQueryCaseObjects}
            handleUpdateFields={handleUpdateFields}
            handleRefreshCaseQueryResult={handleRefreshCaseQueryResult}
            isPreview={isPreview}
            kase={kase}
            module={module}
          />
        );
      }
      case ModuleTypes.Resource: {
        let isLoading: boolean;
        if (module['field-key'] === 'inquiries') {
          isLoading = isLoadingInquiriesDetails;
        } else if (module['field-key'] === 'reports') {
          isLoading = isLoadingReportsDetails;
        } else {
          isLoading = false;
        }

        return (
          <Resource
            module={module}
            fields={objectMap}
            documents={documents}
            inquiryTemplates={inquiryTemplates}
            inquiryTemplateVersions={inquiryTemplateVersions}
            inquiryVerifications={inquiryVerifications}
            allInquiryTemplates={allInquiryTemplates}
            featureFlags={featureFlags}
            transactionStatuses={transactionStatuses}
            transactionTypes={transactionTypes}
            accountStatuses={accountStatuses}
            accountTypes={accountTypes}
            reportTemplates={reportTemplates}
            tags={tags}
            isLoading={isLoading}
            handleUpdateInquiryTags={handleUpdateInquiryTags}
            handleFlagInquiryIssues={handleFlagInquiryIssues}
            handleFlagVerificationIssues={handleFlagVerificationIssues}
            handleUpdateReportTags={handleUpdateReportTags}
          />
        );
      }
      case ModuleTypes.DataDisplay:
        return (
          <DataDisplay
            module={module}
            data={getCasesData(kase.attributes.fields, caseDataManager)}
            isPreview={isPreview}
          />
        );
      case ModuleTypes.DataTable:
        return (
          <DataTableContextAdapter
            resolveVariable={useResolveCaseVariableQueryState}
            module={module}
            token={kase.id}
          />
        );
      case ModuleTypes.Input:
        return (
          <Input
            fieldSchemas={caseTemplateFieldSchema}
            module={module}
            caseDataManager={caseDataManager}
            isPreview={isPreview}
            updateCaseField={handleUpdateFields}
          />
        );
      case ModuleTypes.InputSelect:
        return (
          <InputSelect
            module={module}
            fieldSchema={caseTemplateFieldSchema}
            caseDataManager={caseDataManager}
            isPreview={isPreview}
            updateCaseField={handleUpdateFields}
          />
        );
      case ModuleTypes.KeyValue:
        return <KeyValue module={module} case={kase} isPreview={isPreview} />;
      case ModuleTypes.CaseAttachments:
        return (
          <CaseAttachments
            case={kase}
            handleAddCaseAttachment={handleAddCaseAttachment}
            handleDeleteCaseAttachment={handleDeleteCaseAttachment}
            showMessage={showMessage}
            isPreview={isPreview}
            isPrint={isPrint}
          />
        );
      case ModuleTypes.Markdown: {
        const caseFields = Object.keys(kase.attributes.fields).reduce(
          (attrs, key) => ({
            ...attrs,
            [key]: kase.attributes.fields[key]?.value,
          }),
          {},
        );

        // [chris] TODO: Keeping the fallback to data['metadata'] until Case Templates using the markdown module have been migrated
        const templateData = isEmpty(kase.attributes.fields)
          ? { data: kase.attributes.data['metadata'] }
          : {
              ...caseFields,
              ...caseDataManager,
            };

        return <Markdown module={module} templateData={templateData} isPreview={isPreview} />;
      }
      case ModuleTypes.AISummary: {
        return (
          <AISummary
            module={module}
            generateSummary={caseApi.handleGenerateSummary}
            isPreview={isPreview}
          />
        );
      }
      case ModuleTypes.LayoutByAccount:
        return (
          <LayoutByAccount
            module={module}
            accounts={objectMap.accounts as Account[]}
            accountStatuses={accountStatuses}
            dataApi={caseApi}
            baseComponent={CaseModuleBase}
            runCodeWithContext={runCodeWithContext}
          />
        );
      case ModuleTypes.ListMatches:
        return (
          <ListMatches
            module={module}
            lists={lists}
            listMatches={listMatches}
            listItems={listItems}
            // NOTE: list matches only come from inquiry details right now
            isLoading={isLoadingInquiriesDetails}
          />
        );
      case ModuleTypes.Button: {
        const caseActionWorkflow = caseActionWorkflows.find(
          (actionWorkflow) => actionWorkflow.workflow.id === module['action-id'],
        );

        return (
          <Button
            module={module}
            case={kase}
            caseObjects={caseObjects}
            caseActionWorkflow={caseActionWorkflow}
            caseDataManager={caseDataManager}
            isPreview={isPreview}
            runCodeWithContext={runCodeWithContext}
            handleRunCaseActionWorkflow={handleRunCaseActionWorkflow}
          />
        );
      }
      case ModuleTypes.CaseEmail: {
        const accountEmailAddresses = accounts.flatMap((a) =>
          Object.values(a.attributes.fields || {})
            .map((field) =>
              field.type === FieldType.String && EMAIL_ADDRESS_REGEX.test(field.value)
                ? field.value
                : null,
            )
            .filter((value): value is string => value != null),
        );

        const inquiryEmailAddresses = inquiries.flatMap((i) =>
          Object.values(i.attributes.fields || {})
            .map((field) =>
              field.type === FieldType.String && EMAIL_ADDRESS_REGEX.test(field.value)
                ? field.value
                : null,
            )
            .filter((value): value is string => value != null),
        );

        const caseEmailAddresses = Object.values(kase.attributes.fields || {})
          .map((field) =>
            field.type === FieldType.String && EMAIL_ADDRESS_REGEX.test(field.value)
              ? field.value
              : null,
          )
          .filter((value): value is string => value != null);

        const emailAddresses = [
          ...accountEmailAddresses,
          ...inquiryEmailAddresses,
          ...caseEmailAddresses,
        ];

        const caseObjectsWithRefIds = caseObjects.filter((object) =>
          [ApiModel.Account, ApiModel.Inquiry].includes(object.type),
        ) as (Account | Inquiry)[];
        const defaultCaseObjectRefId =
          caseObjectsWithRefIds.find((object) => object.attributes['reference-id'])?.attributes?.[
            'reference-id'
          ] || '';

        return (
          <CaseEmail
            module={module}
            mails={caseMessages}
            handleCreateEmailDraft={handleCreateEmailDraft}
            handleUpdateEmailDraft={handleUpdateEmailDraft}
            handleSendEmail={handleSendEmail}
            handleDeleteEmail={handleDeleteEmail}
            isPreview={isPreview}
            emailAddresses={uniq(emailAddresses)}
            inquiryTemplates={allInquiryTemplates}
            users={users}
            caseEmailTemplates={caseEmailTemplates}
            defaultCaseObjectRefId={defaultCaseObjectRefId}
          />
        );
      }
      case ModuleTypes.AccountSimilaritySelfie: {
        const accounts = caseObjects.filter(
          (object) => object.type === ApiModel.Account,
        ) as Account[];
        return accounts.map((account) => {
          return <SimilaritySelfie key={account.id} account={account} selfies={[]} />;
        });
      }
      case ModuleTypes.Sar:
        return (
          <SARModule
            sarTokens={objectMap.sars.map((sar) => sar.id)}
            caseDataManager={caseDataManager}
            sarNarrativeTemplates={sarNarrativeTemplates}
            handleCreateSAR={handleCreateSAR}
            handleUpdateSAR={handleUpdateSAR}
            handleAutofillSAR={handleAutofillSAR}
            disableInteraction={isPreview || !hasCaseWritePermission}
          />
        );
      case ModuleTypes.RelatedCases:
        return (
          <RelatedCases
            hiddenCase={kase}
            accounts={accounts}
            currentEnvironment={currentEnvironment}
            users={users}
          />
        );
      case ModuleTypes.FieldUpdate:
        return (
          <FieldUpdate
            fieldSchemas={caseTemplateFieldSchema}
            caseDataManager={caseDataManager}
            updateField={handleUpdateFields}
          />
        );
      case ModuleTypes.InsightsSummary:
        return (
          <InsightsSummary
            module={module}
            isPreview={isPreview}
            runCodeWithContext={runCodeWithContext}
          />
        );
      case ModuleTypes.Table:
        return (
          <Table
            module={module}
            caseDataManager={caseDataManager}
            caseQueryResults={caseQueryResults}
          />
        );
      case ModuleTypes.Iframe:
        return (
          <Iframe title={module.title} url={module.url} runCodeWithContext={runCodeWithContext} />
        );
      default:
        return null;
    }
  };
  return <>{renderModule()}</>;
}
